import { useState, useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import LoginForm from './LoginForm';
import { useMutation } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';

const LoginHandler = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  // 認証状態の監視：ログイン済み（匿名以外）は /admin に自動遷移
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !user.isAnonymous) {
        navigate('/admin');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Firebase エラーの日本語化
  const translateFirebaseError = (code) => {
    switch (code) {
      case 'auth/user-not-found':
        return 'このメールアドレスは登録されていません';
      case 'auth/wrong-password':
        return 'パスワードが正しくありません';
      case 'auth/invalid-email':
        return '無効なメールアドレスです';
      default:
        return 'ログインに失敗しました';
    }
  };

  // ログイン処理
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }) =>
      await signInWithEmailAndPassword(auth, email, password),
    onError: (error) => {
      const msg = translateFirebaseError(error?.code ?? '');
      setErrorMessage(msg);
    },
  });

  // 送信
  const handleLogin = useCallback(() => {
    if (loginMutation.isLoading) return;

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setErrorMessage('メールアドレスとパスワードを入力してください');
      return;
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(trimmedEmail)) {
      setErrorMessage('正しいメールアドレスを入力してください');
      return;
    }

    setErrorMessage('');
    loginMutation.mutate({ email: trimmedEmail, password });
  }, [email, password, loginMutation]);

  return (
    <>
      {/* ★ ここが noindex の明示 */}
      <Helmet>
        <title>ログイン | New‑Nightlife</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <link rel="canonical" href="https://new-nightlife.jp/login" />
      </Helmet>

      <LoginForm
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        handleLogin={handleLogin}
        loading={loginMutation.isLoading}
        errorMessage={errorMessage}
      />
    </>
  );
};

export default LoginHandler;
